import { debounce } from "lodash";
import { useReducer, useCallback, useEffect } from "react";
import { withTranslation } from "react-i18next";

import { Error } from "@krea/common/shared-components/fields/error";
import Fieldset from "@krea/common/shared-components/fields/fieldset";
import TextInput from "@krea/common/shared-components/fields/input";
import { usePostFieldRequest } from "@krea/common/store/field-requests/hooks/mutations";
import { useGetOneFieldRequest } from "@krea/common/store/field-requests/hooks/queries";

import FieldRequestPreviewHeader from "../FieldRequestPreviewHeader";
import FieldRequestSaved from "../FieldRequestSaved";

const PropertyForm = ({ fieldRequestID, isPreviewMode, t }) => {
  const { data: { displayName, submittedData } = {} } =
    useGetOneFieldRequest(fieldRequestID);

  const { mutate: postFieldRequest, error } =
    usePostFieldRequest(fieldRequestID);

  const errors = error?.data?.errors || [];
  const { property_unit_designation = "" } = submittedData;

  function reducer(state, action) {
    switch (action.type) {
      case "property_unit_designation":
        return { ...state, property_unit_designation: action.payload };
      case "touched":
        return { ...state, touched: action.payload };
      default:
        throw new Error();
    }
  }

  const [state, setState] = useReducer(reducer, {
    property_unit_designation,
    touched: false,
  });

  const value = state;

  const createFormData = (value) => {
    const formData = new FormData();
    formData.append(
      "property_unit_designation",
      value.property_unit_designation || "",
    );

    return formData;
  };

  const onInputChange = (e, type) => {
    setState({ type: type, payload: e.target.value });
    setState({ type: "touched", payload: true });
  };

  const onBlur = (state) => {
    const { touched } = state;
    const value = state;
    const formData = createFormData(value);

    if (touched) {
      postFieldRequest(formData);
      setState({ type: "touched", payload: false });
    }
  };

  const getTitleText = (data) => {
    if (data) {
      return `#${data.property_unit_designation}`;
    }
  };

  const debouncedFunc = debounce((state) => onBlur(state), 2500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback((state) => debouncedFunc(state), []);

  useEffect(() => {
    debouncedSave(state);

    return () => {
      debouncedFunc.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <>
      {isPreviewMode ? (
        <>
          <FieldRequestPreviewHeader
            label={t("iaq.property.options.property_unit_designation")}
            value={submittedData && getTitleText(submittedData)}
          />
        </>
      ) : (
        <>
          <form
            className="input-form mb-5"
            onBlur={() => onBlur(state)}
            onSubmit={(e) => e.preventDefault()}
          >
            <Fieldset>
              <TextInput
                autoFocus={true}
                type="text"
                inputMode="text"
                value={value.property_unit_designation || ""}
                onChange={(e) => {
                  onInputChange(e, "property_unit_designation");
                }}
                placeholder={t(
                  "iaq.property.options.property_unit_designation_placeholder",
                )}
                error={
                  errors?.find((e) => e.field === "property_unit_designation")
                    ?.message
                }
              />
            </Fieldset>
          </form>

          <FieldRequestSaved fieldRequestID={fieldRequestID} />
        </>
      )}
    </>
  );
};

export default withTranslation()(PropertyForm);
